import { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { formatDate } from '../utils/date';

const CALENDAR_START = new Date(1900, 0);
const CALENDAR_END = new Date(new Date().getFullYear() + 10, 11);

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Date input with a calendar popover picker.
 * Displays dd/mm/yyyy format. Value is YYYY-MM-DD string.
 */
export function DateInput({ value, onChange, className = '', disabled, placeholder = 'zz/ll/aaaa' }: DateInputProps) {
  const [open, setOpen] = useState(false);

  const displayValue = value ? formatDate(value + 'T00:00:00') : '';
  const selected = value ? new Date(value + 'T00:00:00') : undefined;

  function handleSelect(day: Date | undefined) {
    if (!day) return;
    const yyyy = day.getFullYear();
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={`${className} flex items-center justify-between text-left ${!displayValue ? 'text-muted-foreground' : ''}`}
        >
          <span>{displayValue || placeholder}</span>
          <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[9999] w-auto p-0" align="start">
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          defaultMonth={selected}
          weekStartsOn={1}
          captionLayout="dropdown"
          startMonth={CALENDAR_START}
          endMonth={CALENDAR_END}
          className="p-3"
        />
      </PopoverContent>
    </Popover>
  );
}

interface DateTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Datetime input with a calendar popover + native time picker.
 * Displays dd/mm/yyyy hh:mm format. Value is datetime-local string (YYYY-MM-DDTHH:mm).
 */
export function DateTimeInput({ value, onChange, className = '', disabled, placeholder = 'zz/ll/aaaa hh:mm' }: DateTimeInputProps) {
  const [open, setOpen] = useState(false);

  let displayValue = '';
  let selected: Date | undefined;
  let hours = '12';
  let minutes = '00';

  if (value) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      selected = d;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      hours = String(d.getHours()).padStart(2, '0');
      minutes = String(d.getMinutes()).padStart(2, '0');
      displayValue = `${day}/${month}/${year} ${hours}:${minutes}`;
    }
  }

  function buildValue(date: Date, h: string, m: string): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${h}:${m}`;
  }

  function handleDaySelect(day: Date | undefined) {
    if (!day) return;
    onChange(buildValue(day, hours, minutes));
  }

  function handleTimeChange(timeStr: string) {
    if (!timeStr) return;
    const [h, m] = timeStr.split(':');
    if (h === undefined || m === undefined) return;
    const base = selected ?? new Date();
    onChange(buildValue(base, h, m));
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={`${className} flex items-center justify-between text-left ${!displayValue ? 'text-muted-foreground' : ''}`}
        >
          <span>{displayValue || placeholder}</span>
          <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[9999] w-auto p-0" align="start">
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleDaySelect}
          defaultMonth={selected}
          weekStartsOn={1}
          captionLayout="dropdown"
          startMonth={CALENDAR_START}
          endMonth={CALENDAR_END}
          className="p-3"
        />
        <div className="border-t px-3 py-2 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Ora:</span>
          <input
            type="time"
            value={`${hours}:${minutes}`}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
